import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Package, ShoppingCart, Users, IndianRupee, ArrowUpRight } from "lucide-react";
import { DashboardTutorialCard, useSectionTutorials } from "@/components/TutorialBanner";

import { useTranslation } from "react-i18next";

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const tutorials = useSectionTutorials();

  useEffect(() => {
    api.get("/merchant/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const cards = [
    { label: t("dash.revenue"), value: `₹${(stats?.total_revenue ?? 0).toLocaleString("en-IN")}`, icon: IndianRupee, color: "bg-emerald-500", testid: "stat-revenue" },
    { label: t("dash.orders"), value: stats?.total_orders ?? 0, icon: ShoppingCart, color: "bg-blue-500", testid: "stat-orders" },
    { label: t("dash.products"), value: stats?.total_products ?? 0, icon: Package, color: "bg-orange-500", testid: "stat-products" },
    { label: t("dash.customers"), value: stats?.total_customers ?? 0, icon: Users, color: "bg-fuchsia-500", testid: "stat-customers" },
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{t("dash.title")}</h1>
          <p className="text-slate-500 text-sm mt-1">{t("dash.subtitle")}</p>
        </div>
        <Link to="/admin/products/new" data-testid="dashboard-add-product">
          <Button className="rounded-full bg-blue-600 hover:bg-blue-700">{t("dash.addProduct")}</Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Card key={c.label} data-testid={c.testid} className="p-5 border-slate-200/70 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.04)]">
            <div className="flex items-start justify-between">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{c.label}</div>
              <div className={`w-8 h-8 rounded-lg ${c.color}/10 flex items-center justify-center`} style={{ background: `color-mix(in oklab, currentColor 15%, transparent)` }}>
                <c.icon className={`w-4 h-4 text-white`} style={{ color: c.color.includes("emerald") ? "#059669" : c.color.includes("blue") ? "#2563EB" : c.color.includes("orange") ? "#EA580C" : "#C026D3" }} />
              </div>
            </div>
            <div className="mt-3 font-display text-2xl sm:text-3xl font-extrabold text-slate-900 font-mono-nums break-all">{c.value}</div>
          </Card>
        ))}
      </div>

      <DashboardTutorialCard url={tutorials.dashboard} />

      <Card className="p-6 border-slate-200/70">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold text-slate-900">{t("dash.recentOrders")}</h2>
          <Link to="/admin/orders" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1" data-testid="dashboard-view-all-orders">{t("dash.viewAll")} <ArrowUpRight className="w-3.5 h-3.5" /></Link>
        </div>
        {stats?.recent_orders?.length ? (
          <div className="divide-y divide-slate-100">
            {stats.recent_orders.map((o) => (
              <div key={o.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-mono-nums text-sm text-slate-900">{o.order_number}</div>
                  <div className="text-xs text-slate-500">{o.customer_name} • {o.items?.length || 0} {t("dash.items")}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-nums font-semibold text-slate-900">₹{o.total?.toLocaleString("en-IN")}</div>
                  <div className="text-xs text-slate-500 capitalize">{o.status}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-slate-500">{t("dash.noOrders")}</div>
        )}
      </Card>
    </div>
  );
}
